import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

import { selectUserId } from '~/auth/slices/auth.slice'
import { TopicId } from '~/dashboard/utils/learningPath.utils'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { parseUrlPathParams } from '~/shared/utils/parseUrlPathParams.utils'
import curriculumAnalytics from '~/analytics/curriculum/curriculumAnalytics'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import LearningPathAvatarIcon from '~/dashboard/components/LearningPathAvatarIcon'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'
import { CurriculumGradeLevelType, LevelSource } from '~/curriculum/constants/curriculum.const'
import { LevelActionType } from '~/bootcamp/types/bootcamp.types'

import { CurriculumLevelDataType } from '../types/curriculum.types'
import { selectGradeContent } from '../slice/curriculum.slice'
import { useGetCurriculumLastActivityQuery } from '../apis/get-curriculum-user-stats.api'
import getBootcampLevelPage from '../utils/getBootcampLevelPage'
import { selectScrollItemIdOnTopicPage, setScrollItemIdOnTopicPage } from '../slice/persistCurriculum.slice'

import AiLevel from './AiLevel'
import BootcampLevel from './BootcampLevel'
import MathVaultLevel from './MathVaultLevel'
import EncyclopediaLevel from './EncyclopediaLevel'

interface Props {
  topic: TopicId
  curriculumLevel: CurriculumLevelDataType
  id: string
  quarterNo: number
}

export default function CurriculumGradeLevel({ topic, curriculumLevel, id, quarterNo }: Props): React.ReactElement {
  const dispatch = useAppDispatch()
  const gradeData = useAppSelector(selectGradeContent)
  const userId = useAppSelector(selectUserId)
  const { type, iconType, title, path, prompt, globalId, isCompleted } = curriculumLevel
  const params = path ? parseUrlPathParams('/bootcamp/:category/:subjectId/:bootcampId/:levelId/:action', path) : null

  const avatarRef = useRef<HTMLDivElement>(null)
  const curriculumLevelRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const scrollItemId = useAppSelector(selectScrollItemIdOnTopicPage)

  const lastActivity = useGetCurriculumLastActivityQuery(
    { eventName: EventType.CURRICULUM_EVENT },
    {
      skip: !userId,
    },
  ).data

  const bootcampLevelId = params
    ? [
        params.subjectId,
        params.bootcampId,
        params.levelId,
        getBootcampLevelPage(params.action as LevelActionType),
      ].join('#')
    : ''

  const mathVaultLevelId = `${globalId}#${path?.split('/')[5]}`
  const curriculumLevelId =
    type === CurriculumGradeLevelType.BOOTCAMP
      ? bootcampLevelId
      : type === CurriculumGradeLevelType.MATH_VAULT
      ? mathVaultLevelId
      : type === CurriculumGradeLevelType.ENCYCLOPEDIA
      ? path
      : curriculumLevel.curriculumLevelId

  const handleGradeLevelAnalytics = ({
    gradeLevelType,
    gradeLevelTitle,
    subjectId,
    bootcampId,
    levelId,
    aiPrompt,
  }: {
    gradeLevelType: 'bootcamp' | 'ai_prompt'
    gradeLevelTitle: string
    subjectId?: string
    bootcampId?: string
    levelId?: string
    aiPrompt?: string
  }): void => {
    if (gradeLevelType === 'bootcamp') {
      curriculumAnalytics.logGradeLevelClick(gradeLevelType, gradeLevelTitle, {
        level: levelId as string,
        subjectId: subjectId as string,
        bootcampId: bootcampId as string,
        source: LevelSource.CURRICULUM,
      })
    } else {
      curriculumAnalytics.logGradeLevelClick(
        gradeLevelType,
        gradeLevelTitle,
        {
          prompt: aiPrompt,
          source: LevelSource.CURRICULUM,
        },
        'v1',
      )
    }
  }

  const isLastVisited = lastActivity?.curriculumLevelId === curriculumLevelId

  useEffect(() => {
    if (scrollItemId === curriculumLevelId && curriculumLevelRef.current) {
      curriculumLevelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [curriculumLevelId, scrollItemId])

  const onCurriculumItemClick = async (curriculumTitle: string): Promise<void> => {
    if (userId) {
      const extra = {
        title: curriculumTitle,
        curriculumLevelId,
        quarterNo,
        gradeLevel: gradeData.gradeLevel,
        grade: gradeData.gradeLevel?.toString(),
        topic: gradeData.title,
        subTopic: curriculumTitle,
        levelTitle: curriculumTitle,
        topicId: topic,
      }
      const event = {
        eventName: EventType.CURRICULUM_EVENT,
        viewKey: router.asPath,
        extra: {
          detectBy: EventDetectionMethod.ON_SELECT_CURRICULUM_LEVEL,
          ...extra,
        },
      }
      dispatch(setScrollItemIdOnTopicPage(curriculumLevelId ?? ''))
      await logUserActivity(userId, event)
    }
  }

  return (
    <div className="w-full px-2 md:px-0 md:hover:scale-110 duration-300 z-1 relative" id={id} ref={curriculumLevelRef}>
      {type === CurriculumGradeLevelType.BOOTCAMP && (
        <BootcampLevel
          iconType={iconType}
          isCompleted={isCompleted}
          levelTitle={title}
          onClick={onCurriculumItemClick}
          path={path}
          source={LevelSource.CURRICULUM}
          topic={topic}
        />
      )}
      {type === CurriculumGradeLevelType.AI && curriculumLevel.curriculumLevelId && (
        <AiLevel
          curriculumLevelId={curriculumLevel.curriculumLevelId}
          handleGradeLevelAnalytics={handleGradeLevelAnalytics}
          isCompleted={isCompleted}
          levelTitle={title}
          onClick={onCurriculumItemClick}
          prompt={prompt as string}
          topic={topic}
        />
      )}
      {type === CurriculumGradeLevelType.ENCYCLOPEDIA && (
        <EncyclopediaLevel
          isCompleted={isCompleted}
          levelTitle={title}
          onClick={onCurriculumItemClick}
          path={path}
          topic={topic}
        />
      )}
      {type === CurriculumGradeLevelType.MATH_VAULT && (
        <MathVaultLevel
          globalId={globalId as string}
          isCompleted={isCompleted}
          levelTitle={title}
          onClick={onCurriculumItemClick}
          path={path}
          source={LevelSource.CURRICULUM}
          topic={topic}
        />
      )}
      {isLastVisited && <LearningPathAvatarIcon avatarRef={avatarRef} className="-right-6 md:-right-7 -top-3" />}
    </div>
  )
}
